import { injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { ACDLError } from '../../__legacy__/models/error.model';
import { IThresholdConfig } from './jobs.api.model';

export interface IJobThreshold {
  maxScore: number;
  minScore: number;
  optimalThreshold: number;
  thresholdStep: number;
  minThreshold: number | null;
  maxThreshold: number | null;
}

export interface IJobThresholdStore {
  threshold: IJobThreshold | null;
  jobId: string | null;
  setThreshold(jobId: string, threshold: IThresholdConfig | null): void;
  setThresholdRange: (min: number, max: number) => void;
  setOptimalThreshold: (optimal: number) => void;
}

export namespace IJobThresholdStore {
  export const $: interfaces.ServiceIdentifier<IJobThresholdStore> = Symbol('THRESHOLD_STORE');
}

@injectable()
export class JobThresholdStore implements IJobThresholdStore {
  jobId: string | null = null;
  threshold: IJobThreshold | null = null;

  constructor() {
    makeObservable(this, {
      jobId: observable,
      threshold: observable,
      setThreshold: action,
      setThresholdRange: action,
      setOptimalThreshold: action,
    });
  }

  setThreshold = (jobId: string, threshold: IThresholdConfig | null): void => {
    this.threshold = threshold
      ? {
          maxScore: threshold.max_score,
          maxThreshold: threshold.max_threshold,
          minScore: threshold.min_score,
          minThreshold: threshold.min_threshold,
          optimalThreshold: threshold.optimal_threshold,
          thresholdStep: threshold.threshold_step,
        }
      : null;
    this.jobId = jobId;
  };

  setThresholdRange = (min: number, max: number): void => {
    if (!this.threshold) {
      throw new ACDLError('Tried to set uninitialized threshold');
    }

    if (min < 0) {
      this.threshold.minThreshold = min;
      return;
    }

    if (max > 255) {
      this.threshold.maxThreshold = max;
      return;
    }

    if (min > max) {
      this.threshold.minThreshold = max;
      return;
    }

    this.threshold.minThreshold = min;
    this.threshold.maxThreshold = max;
  };

  setOptimalThreshold = (optimal: number): void => {
    if (!this.threshold) {
      throw new ACDLError('Tried to set uninitialized threshold');
    }

    this.threshold.optimalThreshold = optimal;
  };
}
